import React, { useContext, useEffect, useState, ReactElement } from "react";
import { useTextDomainContext } from "@/app/providers";
import * as Yup from "yup";
import { useFormik } from "formik";
import {
  Alert,
  Box,
  Button,
  Collapse,
  Grid,
  IconButton,
  InputAdornment,
  OutlinedInput,
  Stack,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useResetPassword } from "@/hooks";
interface ResetPasswordFormProps {
  token?: string;

  setResetPassword?: (value: boolean) => void;
}

interface FormValues {
  newPassword: string;
  confirmPassword: string;
  submit?: string;
}

/**
 * ResetPasswordForm component renders a form for resetting the user's password.
 * It includes password fields with visibility toggle and form validation.
 */
export const ResetPasswordForm = (
  props: ResetPasswordFormProps,
): ReactElement => {
  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);

  const { token, setResetPassword } = props;

  const [disableButton, setDisableButton] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] =
    useState<boolean>(false);
  const [showNewPassword, setShowNewPassword] = useState<boolean>(false);

  const validationSchema = Yup.object().shape({});

  const formik = useFormik<FormValues>({
    initialValues: {
      newPassword: "",
      confirmPassword: "",
      submit: undefined,
    },
    onSubmit: (values: FormValues) => {
      submit({
        password1: values.newPassword,
        password2: values.confirmPassword,
        token: (token || "").replace(/ /g, "+"),
      });
    },
    validationSchema,
  });

  const { submit, isSubmitting, resetSuccess } = useResetPassword(
    () => {
      // Success callback
    },
    (error: Error | null) => {
      if (error) {
        formik.setFieldError("submit", error.toString());
      } else {
        formik.setFieldError("submit", gettext("Unknown Error Occurred !"));
      }
    },
  );

  const handleClickShowConfirmPassword = (): void => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleClickShowNewPassword = (): void => {
    setShowNewPassword(!showNewPassword);
  };

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>,
  ): void => {
    event.preventDefault();
  };

  useEffect(() => {
    if (resetSuccess === true) {
      formik.setFieldError("submit", "Password Reset Successful!");
    }
    if (setResetPassword) {
      setResetPassword(resetSuccess || false);
    }
  }, [resetSuccess, setResetPassword, formik]);

  useEffect(() => {
    if (token === undefined || token === null) {
      setTimeout(() => {
        setDisableButton(true);
      }, 0);
      formik.setValues((values: FormValues) => ({
        ...values,
        submit: "Valid token link is required",
      }));
    }
  }, [token, formik]);

  return (
    <Box
      sx={{
        marginY: 1,
      }}
    >
      {!resetSuccess ? (
        <>
          <Collapse in={Boolean(formik.values.submit)}>
            <Alert
              variant="outlined"
              severity={resetSuccess ? "success" : "error"}
            >
              {formik.values.submit}
            </Alert>
          </Collapse>

          <form noValidate onSubmit={formik.handleSubmit}>
            <Grid
              container
              spacing={2}
              sx={{
                mt: 1,
              }}
            >
              <Grid size={{ xs: 12 }}>
                <Stack>
                  <OutlinedInput
                    id="password-login"
                    name="newPassword"
                    type={showNewPassword ? "text" : "password"}
                    fullWidth
                    size="medium"
                    placeholder={gettext("New Password")}
                    value={formik.values.newPassword}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    endAdornment={
                      <InputAdornment
                        position="end"
                        sx={{
                          color: "#D4D4D8",
                        }}
                      >
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowNewPassword}
                          onMouseDown={handleMouseDownPassword}
                          edge="end"
                          color="inherit"
                          size="small"
                        >
                          {showNewPassword ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                      </InputAdornment>
                    }
                  />
                  {formik.touched.newPassword && formik.errors.newPassword ? (
                    <div className="fv-plugins-message-container">
                      <div className="fv-help-block">
                        {formik.errors.newPassword}
                      </div>
                    </div>
                  ) : null}
                </Stack>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Stack>
                  <OutlinedInput
                    id="password-confirm"
                    name="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    fullWidth
                    size="medium"
                    placeholder={gettext("Confirm Password")}
                    value={formik.values.confirmPassword}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    endAdornment={
                      <InputAdornment
                        position="end"
                        sx={{
                          color: "#D4D4D8",
                        }}
                      >
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowConfirmPassword}
                          onMouseDown={handleMouseDownPassword}
                          edge="end"
                          color="inherit"
                          size="small"
                        >
                          {showConfirmPassword ? (
                            <Visibility />
                          ) : (
                            <VisibilityOff />
                          )}
                        </IconButton>
                      </InputAdornment>
                    }
                  />

                  {formik.touched.confirmPassword &&
                  formik.errors.confirmPassword ? (
                    <div className="fv-plugins-message-container">
                      <div className="fv-help-block">
                        {formik.errors.confirmPassword}
                      </div>
                    </div>
                  ) : null}
                </Stack>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Button
                  disableElevation
                  disabled={disableButton ? disableButton : isSubmitting}
                  fullWidth
                  size="large"
                  type="submit"
                  variant="contained"
                  sx={{ color: "primary" }}
                >
                  {gettext("Reset Password")}
                </Button>
              </Grid>
            </Grid>
          </form>
        </>
      ) : (
        <>
          <Grid
            container
            spacing={2}
            sx={{
              mt: 1,
            }}
          >
            <Grid size={{ xs: 12 }}>
              <a href="/auth/login">
                <Button
                  disableElevation
                  fullWidth
                  size="large"
                  variant="contained"
                  sx={{ color: "primary" }}
                >
                  {gettext("Sign In")}
                </Button>
              </a>
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  );
};
